import { defineStore } from 'pinia'
import { markRaw, toRaw } from 'vue'
import type { CategoryMenuItem, CategoryTreeItem } from '~/types/category'
import { decodeCategories } from '~/helpers/categoryCodec'

/** Category list -> tree (children in the list order). Roots are the categories without a parent. */
function buildTree(categories: CategoryMenuItem[]): CategoryTreeItem[] {
  const nodes = new Map<number, CategoryTreeItem>()
  categories.forEach(category => nodes.set(category.id, { ...category, children: [] }))

  const tree: CategoryTreeItem[] = []
  categories.forEach((category) => {
    const node = nodes.get(category.id)!
    if (!category.parentId) {
      tree.push(node)
    }
    else {
      nodes.get(category.parentId)?.children.push(node)
    }
  })
  return tree
}

export const useCategoryStore = defineStore('category', {
  state: () => ({
    // Menu fields only (server/utils/categories.ts). The state is sent with every page, so keep it small:
    // the tree is derived from this list (getCategoryTree) instead of being stored a second time.
    // Static data, so not reactive (markRaw): with ~1700 categories, Vue's dependency tracking of every
    // field read during server rendering cost ~28 000 tracking entries per request and ran production pods
    // out of memory under load (15.10.2026).
    categories: [] as CategoryMenuItem[],
    fetched: false
  }),

  actions: {
    /** All categories from /api/categories (cached on the server). The browser gets the compact format (helpers/categoryCodec.ts). */
    async fetchCategories() {
      try {
        this.categories = markRaw(import.meta.server
          ? await $fetch<CategoryMenuItem[]>('/api/categories')
          : decodeCategories(await $fetch<string>('/api/categories', { query: { format: 'compact' }, responseType: 'text' })))
        this.fetched = true
        return true
      }
      catch (error) {
        // @TODO: Add Sentry logging
        console.error(error)
        return false
      }
    },

    /** The list that came with the page (plugins/init.ts) */
    setCategories(categories: CategoryMenuItem[]) {
      this.categories = markRaw(categories)
      this.fetched = true
    }
  },

  getters: {
    getCategoryByPath: state => (path: string): CategoryMenuItem | undefined => {
      return state.categories.find((c: CategoryMenuItem) => c.path === path)
    },
    getSubCategoriesByPath: state => (path: string): CategoryMenuItem[] => {
      const category = state.categories.find((c: CategoryMenuItem) => c.path === path)
      return state.categories.filter(cat => category?.children?.includes(cat.id))
    },
    getCategoryById: state => (id: CategoryMenuItem['id']): CategoryMenuItem | undefined => {
      return state.categories.find((c: CategoryMenuItem) => c.id === id)
    },
    getCategoryByName: state => (name: string): CategoryMenuItem | undefined => {
      return state.categories.find((c: CategoryMenuItem) => c.name === name)
    },
    getMainCategories: (state): CategoryMenuItem[] => {
      return state.categories.filter((c: CategoryMenuItem) => c.level === 1 && c.isIncludedInMenu).sort((a: CategoryMenuItem, b: CategoryMenuItem) => (a.sortOrder || 0) - (b.sortOrder || 0))
    },
    getCategoryTree: (state): CategoryTreeItem[] => {
      // From the raw list: building the tree inside a getter (computed) would otherwise track every field read
      return markRaw(buildTree(toRaw(state.categories)).sort((a: CategoryTreeItem, b: CategoryTreeItem) => (a.sortOrder || 0) - (b.sortOrder || 0)))
    },
    getPopularCategories: (state): CategoryMenuItem[] => {
      return state.categories.filter((c: CategoryMenuItem) => c.popular === true && !c.path?.startsWith('brandit/'))
    },
    getPopularBrands: (state): CategoryMenuItem[] => {
      return state.categories.filter((c: CategoryMenuItem) => c.popular === true && c.path?.startsWith('brandit/'))
    },
    isFetched: state => state.fetched
  }
})
